import './Admission.css';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';
import SpinnerLoader from '../Components/SpinnerLoader';
import Lottie from 'lottie-react';
import MailSent from '../MailSent.json';
import logger from '../Components/Logger';
import { useLevelLabel } from '../Components/LevelName';
import React, { useState, useRef, useEffect } from "react";


// Source code https://stackoverflow.com/questions/59595700/how-to-make-a-react-component-fade-in-on-scroll-using-intersectionobserver-but 
// + ChatGPT fixing small bugs in layout.
const FadeInSection = ({ children }) => {
  const domRef = useRef(null);
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisible(true);
        observer.unobserve(domRef.current);
        logger.info("FadeInSection is now visible on the screen.");
      }
    });

    if (domRef.current) {
      observer.observe(domRef.current);
      logger.info("IntersectionObserver is now observing the FadeInSection component.");
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={domRef}
      className={`fade-section ${isVisible ? "is-visible" : ""}`}
    >
      {children}
    </section>
  );
};


// Admission Application Form Component
// source : https://youtu.be/H63Pd_lXkeQ?si=DbhCl3gG2GRlT1xT , https://vee-validate.logaretm.com/v4/guide/composition-api/handling-forms/

export function Admission({t}) {
    const elementaryLabel = useLevelLabel("elementary");
    const highSchoolLabel = useLevelLabel("highschool");
    const cegepLabel = useLevelLabel("cegep");

    const [isLoading, setIsLoading] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [isCooldown, setIsCooldown] = useState(false);

    const defaultValues = {
        firstName: "",
        lastName: "",
        gradeLevel: "",
        schoolName: "",
        parentFirstName: "",
        parentLastName: "",
        contactNumber: "",
        email: "",
        tutoringPreference: [],
        otherSubject: "",
        tutoringType: "",
        availability: "",
        additionalInfo: "",
        termsAndConditions: false,
    };

    const { register, handleSubmit, reset, setValue, watch,formState: { errors }} = useForm({
        defaultValues,
    });

    const selectedOther = watch("tutoringPreference")?.includes("Other");

    // Watch the "Other" subject preference to conditionally 
    // require the "otherSubject" field and reset it when "Other" 
    // is unchecked to maintain form integrity and prevent stale data.
    useEffect(() => {
        if (!selectedOther) {
            setValue("otherSubject", "");
        }
    }, [selectedOther, setValue]);

    // Watch the tutoring type to dynamically update availability options and reset availability 
    // when tutoring type changes to prevent stale data and ensure form integrity.
    // Source : ChatGPT
    const tutoringType = watch("tutoringType");

    useEffect(() => {
        if (!tutoringType) {
            setValue("availability", "");
        }
        if (tutoringType === "One-on-One") {
            setValue("availability", "Weekends (one-on-one only) (1:00 PM - 3:00 PM)");
        }
    }, [tutoringType, setValue]);

    // Reset isSent after 5 seconds to hide success message after form submission and corrected by ChatGPT
    useEffect(() => {
        if (!isSent) return;
        const timer = setTimeout(() => setIsSent(false), 5000);
        return () => clearTimeout(timer);
    }, [isSent]);


    // EmailJS configuration
    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;    
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;   
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;


    // source: https://forum.freecodecamp.org/t/build-a-telephone-number-validator-project/679577
    const phoneFlex = /^\(\d{3}\) \d{3} - \d{4}$/; 
   
    // Source: Google search "javascript format phone number as (123) 456 - 7890 Regex"
    const formatPhone = (value) => {
        const cleaned = ('' + value).replace(/\D/g, '').slice(0, 10);
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        return match ? `(${match[1]}) ${match[2]} - ${match[3]}` : cleaned;
    };

    // Form submission handler corrected by ChatGPT and https://youtu.be/Nm_IHH4iOx4?si=tY3DgADMV5GJ0Jzl
    const onSubmit = (data) => {
        const tutoringArray = Array.isArray(data.tutoringPreference)
            ? data.tutoringPreference
            : data.tutoringPreference
            ? [data.tutoringPreference]
            : [];

        const payload = {
            ...data,
            tutoringPreference: tutoringArray.join(", "),
        };

        console.log("Form Data:", payload);

        setIsLoading(true);
        setIsSent(false);

        // Send email using EmailJS
        emailjs
            .send(SERVICE_ID, TEMPLATE_ID, payload, PUBLIC_KEY)
            .then((res) => {
                //alert("Form submitted successfully");
                document.activeElement?.blur();
                reset(defaultValues);
                setIsSent(true);
                console.log("EmailJS success:", res);
                logger.info("Form submitted successfully.");
                setIsCooldown(true);
                setTimeout(() => setIsCooldown(false), 60000);
        })
        .catch((err) => {
            console.error("EmailJS error:", err);
            alert(err?.text || "An error occurred while submitting the form.");
            logger.error("Failed to submit form.");
        })
        .finally(() => {
            setIsLoading(false);
        });

    };
    
    return (    
        <div className="admission-page">
            <div className="container">
                <FadeInSection>
                <div className="card">
                    <h1>{t.AdmissionTitle}</h1>
                    <p>{t.AdmissionDescription}</p>
                    <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="section-title">{t.StudentInformation}</div>
                    <hr/>
                    <div className="grid">
                        <div className="form-group">
                            <label htmlFor="firstName">{t.StudentFirstName} <span className="required">*</span></label>
                            <input type="text" id="firstName" {...register("firstName")}required/>
                        </div>
                        <div className="form-group">
                            <label htmlFor="lastName">{t.StudentLastName} <span className="required">*</span></label>
                            <input type="text" id="lastName" {...register("lastName")} required/>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="form-group">
                            <label htmlFor="gradeLevel">{t.StudentGradeLevel} <span className="required">*</span></label>
                            <select id="gradeLevel" {...register("gradeLevel")} defaultValue="" required>
                                <option value="" disabled>{t.SelectGradeLevel}</option>
                                <option value="Elementary" translate="no">{elementaryLabel}</option>
                                <option value="High School" translate="no">{highSchoolLabel}</option>
                                <option value="Cegep" translate="no">{cegepLabel}</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="schoolName">{t.SchoolName} <span className="required">*</span></label>
                            <input type="text" id="schoolName" {...register("schoolName")} required/>
                        </div>
                    </div>

                    <div className="section-title">{t.ParentGuardianInformation}</div>
                    <hr/>
                    <div className="grid">
                        <div className="form-group">
                            <label htmlFor="parentFirstName">{t.ParentFirstName} <span className="required">*</span></label>
                            <input type="text" id="parentFirstName" {...register("parentFirstName")} required/>
                        </div>
                        <div className="form-group">
                            <label htmlFor="parentLastName">{t.ParentLastName} <span className="required">*</span></label>
                            <input type="text" id="parentLastName" {...register("parentLastName")} required/>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="form-group">
                            <label htmlFor="contactNumber">{t.ParentPhone} <span className="required">*</span></label>
                            <input type="tel" id="contactNumber" 
                                {...register("contactNumber", {
                                pattern: phoneFlex
                            })}
                            onChange={(e) => setValue("contactNumber", formatPhone(e.target.value))}
                            required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">{t.ParentEmail} <span className="required">*</span></label>
                            <input type="email" id="email" {...register("email", {pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })} required />
                        </div>
                    </div>

                    <div className="section-title">{t.TutoringPreferences}</div>
                    <hr/>
                    <div className="grid">
                        <div className="form-group">
                            <label>{t.SubjectPreferences}</label>
                            {errors.tutoringPreference && <p className="error-text">{errors.tutoringPreference.message}</p>}
                            <div className="checkbox-group">
                                <label>
                                    <input type="checkbox" value="French" {...register("tutoringPreference", {
                                        validate: (value) => value.length > 0 || t.PleaseSelectAtLeastOneSubject,
                                    })} />
                                    <span>{t.French}</span>
                                </label>
                                <label>
                                    <input type="checkbox" value="Math" {...register("tutoringPreference")} />
                                    <span>{t.Math}</span>
                                </label>
                                <label>
                                    <input type="checkbox" value="Science" {...register("tutoringPreference")} />
                                    <span>{t.Science}</span>
                                </label>
                                <label>
                                    <input type="checkbox" value="English" {...register("tutoringPreference")} />
                                    <span>{t.English}</span>
                                </label>
                                <label>
                                    <input type="checkbox" value="History" {...register("tutoringPreference")} />
                                    <span>{t.History}</span>
                                </label>
                                <label>
                                    <input type="checkbox" value="Other" {...register("tutoringPreference")} />
                                    <span>{t.Other}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="form-group full-with">
                            <label htmlFor="otherSubject">{t.IfOther}</label>
                            <textarea id="otherSubject" disabled={!selectedOther} {...register("otherSubject",
                                {validate: value =>
                                    selectedOther
                                        ? value.trim() !== "" || t.PleaseSpecifyTheOtherSubject
                                        : true
                                })} placeholder={t.Specifysubject}>
                            </textarea>
                            {errors.otherSubject && <p className="error-text">{errors.otherSubject.message}</p>}
                        </div>
                    </div>
                    <div className="grid">
                        <div className="form-group">
                            <label htmlFor="tutoringType">{t.TutoringType} <span className="required">*</span></label>
                            <select id="tutoringType" {...register("tutoringType")} defaultValue="" required>
                                <option value="" disabled>{t.TutoringTypeSelect}</option>
                                <option value="One-on-One">{t.OneonOne}</option>
                                <option value="Group Tutoring">{t.Group}</option>
                            </select>

                        </div>
                        <div className="form-group">
                            <label htmlFor="availability">{t.Availability} <span className="required">*</span></label>
                            <select
                                id="availability"
                                {...register("availability")}
                                value={watch("availability") || ""}
                                onChange={(e) => setValue("availability", e.target.value)}
                                disabled={!tutoringType}
                                required
                                >
                                <option value="" disabled>{t.SelectAvailability}</option>

                                {tutoringType === "One-on-One" ? (
                                    <option value="Weekends (1:00 PM - 3:00 PM)"> 
                                    {t.OneonOneTimes}
                                    </option>
                                ) : (
                                    <>
                                    <option value="Weekdays (4:30 PM - 7:30 PM)">
                                       {t.GroupTimesWeekDays}
                                    </option>
                                    <option value="Weekends (11:00 AM - 1:00 PM)">
                                        {t.GroupTimesWeekends}
                                    </option>
                                    </>
                                )}
                            </select>

                        </div>
                    </div>

                    <div className="grid">
                        <div className="form-group full-with">
                            <label htmlFor="additionalInfo">{t.AdditionalInformation}</label>
                            <textarea id="additionalInfo" {...register("additionalInfo")} rows="4" placeholder={t.AnySpecificNeedsOrRequests}></textarea>
                        </div>
                    </div>

                    <div className="checkbox-termsConditions">
                        <label>
                            <input type="checkbox" {...register("termsAndConditions")} required/>
                            <span className="required">*</span>
                            <span>{t.termsAndConditions}</span>
                        </label>
                    </div>
                    
                    {isLoading && (
                        <div className="loading-overlay">
                            <SpinnerLoader />
                        </div>
                    )}

                    {isSent && (
                        <div className="success-message">
                            <Lottie animationData={MailSent} loop={false} autoplay={true}/>
                            <p>{t.FormSubmittedSuccessfully}</p>
                        </div>
                    )}

                    <button type="submit" className={`submit-btn ${isCooldown ? "btn-cooldown" : ""}`} disabled={isLoading || isCooldown}>
                        {isLoading ? "Loading..." : isCooldown ? t.PleaseWait || "Please wait..." : t.SubmitApplication}
                    </button>
                </form>
            </div>
            </FadeInSection>
        </div>
    </div>
);}